// Shared TypeScript types for AuditFlow AI legal assistant

export interface AuditFlowFolder {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  parent_folder_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditFlowProject {
  id: string;
  user_id: string;
  is_owner?: boolean;
  name: string;
  cm_number: string | null;
  shared_with: string[];
  created_at: string;
  updated_at: string;
  documents?: AuditFlowDocument[];
  folders?: AuditFlowFolder[];
  document_count?: number;
  chat_count?: number;
  review_count?: number;
}

export interface AuditFlowDocument {
  id: string;
  user_id?: string;
  project_id: string | null;
  folder_id?: string | null;
  filename: string;
  file_type: string | null; // pdf | docx | doc
  storage_path: string | null;
  pdf_storage_path: string | null;
  size_bytes: number | null;
  page_count: number | null;
  structure_tree: StructureNode[] | null;
  status: "pending" | "processing" | "ready" | "error";
  created_at: string | null;
  updated_at?: string | null;
  /** Max version_number across assistant_edit rows, null if doc is unedited. */
  latest_version_number?: number | null;
}

export interface StructureNode {
  id: string;
  title: string;
  level: number;
  page_number: number | null;
  children: StructureNode[];
}

export interface AuditFlowChat {
  id: string;
  project_id: string | null;
  user_id: string;
  title: string | null;
  created_at: string;
}

export interface AuditFlowEditAnnotation {
  type?: "edit_data";
  kind?: "edit";
  edit_id: string;
  document_id: string;
  version_id: string;
  /** Per-document monotonic Vn for the edit's target version. */
  version_number?: number | null;
  change_id: string;
  del_w_id?: string;
  ins_w_id?: string;
  deleted_text: string;
  inserted_text: string;
  context_before?: string;
  context_after?: string;
  reason?: string;
  status: "pending" | "accepted" | "rejected";
}

export type AssistantEvent =
  | { type: "reasoning"; text: string; isStreaming?: boolean }
  | {
        type: "tool_call_start";
        name: string;
        isStreaming?: boolean;
    }
  | { type: "thinking"; isStreaming?: boolean }
  | {
        type: "doc_read";
        filename: string;
        document_id?: string;
        isStreaming?: boolean;
    }
  | {
        type: "doc_find";
        filename: string;
        query: string;
        total_matches: number;
        isStreaming?: boolean;
    }
  | {
        type: "doc_created";
        filename: string;
        download_url: string;
        /** Set when the generated doc is persisted as a first-class document. */
        document_id?: string;
        version_id?: string;
        version_number?: number | null;
        isStreaming?: boolean;
    }
  | { type: "doc_download"; filename: string; download_url: string }
  | {
        type: "doc_replicated";
        /** Source document filename. */
        filename: string;
        /** How many copies were produced in this single tool call. */
        count: number;
        /** One entry per new copy. Empty while streaming. */
        copies?: {
            new_filename: string;
            document_id: string;
            version_id: string;
        }[];
        error?: string;
        isStreaming?: boolean;
    }
  | { type: "workflow_applied"; workflow_id: string; title: string }
  | {
        type: "doc_edited";
        filename: string;
        document_id: string;
        version_id: string;
        /** Per-document monotonic Vn written at emit time. */
        version_number?: number | null;
        download_url: string;
        annotations: AuditFlowEditAnnotation[];
        error?: string;
        isStreaming?: boolean;
    }
  | { type: "content"; text: string; isStreaming?: boolean };

export interface AuditFlowMessage {
  role: "user" | "assistant";
  content: string;
  files?: { filename: string; document_id?: string }[];
  workflow?: { id: string; title: string };
  model?: string;
  annotations?: AuditFlowCitationAnnotation[];
  events?: AssistantEvent[];
  /** Set when streaming failed; rendered as a red error block. */
  error?: string;
}

export interface CitationQuote {
  page: number;
  quote: string;
}

/**
 * A citation emitted by the assistant. Single-page citations have a numeric
 * `page` and a plain `quote`. A citation that spans a page break (one
 * continuous sentence cut by a page boundary) has `page` as a range string
 * like "41-42" and a `quote` containing the `[[PAGE_BREAK]]` sentinel at the
 * break point (text before is on page 41, text after is on page 42).
 */
export interface AuditFlowCitationAnnotation {
  type: "citation_data";
  ref: number;
  doc_id: string;
  document_id: string;
  version_id?: string | null;
  version_number?: number | null;
  filename: string;
  page: number | string;
  quote: string;
}

const PAGE_BREAK_SENTINEL = "[[PAGE_BREAK]]";

/**
 * Expand a citation into one or more (page, quote) entries suitable for
 * highlighting in the PDF viewer. A single-page citation yields one entry; a
 * cross-page citation with page "N-M" and a `[[PAGE_BREAK]]` split yields two.
 */
export function expandCitationToEntries(
  a: AuditFlowCitationAnnotation,
): CitationQuote[] {
  const rangeMatch =
    typeof a.page === "string"
      ? a.page.match(/^(\d+)\s*-\s*(\d+)$/)
      : null;
  if (rangeMatch && a.quote.includes(PAGE_BREAK_SENTINEL)) {
    const startPage = parseInt(rangeMatch[1], 10);
    const endPage = parseInt(rangeMatch[2], 10);
    const [before, after] = a.quote.split(PAGE_BREAK_SENTINEL);
    return [
      { page: startPage, quote: before.trim() },
      { page: endPage, quote: after.trim() },
    ].filter((e) => e.quote.length > 0);
  }
  const pageNum =
    typeof a.page === "number" ? a.page : parseInt(String(a.page), 10);
  if (!Number.isFinite(pageNum)) return [];
  return [{ page: pageNum, quote: a.quote }];
}

/** Format the page(s) of a citation for display, e.g. "Page 3" or "Page 41-42". */
export function formatCitationPage(a: AuditFlowCitationAnnotation): string {
  if (typeof a.page === "string") return `Page ${a.page}`;
  return `Page ${a.page}`;
}

/** Produce a reader-friendly version of the quote (replaces [[PAGE_BREAK]] with "..."). */
export function displayCitationQuote(a: AuditFlowCitationAnnotation): string {
  return a.quote.replaceAll(PAGE_BREAK_SENTINEL, "...");
}

// Tabular Review

export type ColumnFormat =
    | "text"
    | "bulleted_list"
    | "number"
    | "currency"
    | "yes_no"
    | "date"
    | "tag"
    | "percentage"
    | "monetary_amount";

export interface ColumnConfig {
    index: number;
    name: string;
    prompt: string;
    format?: ColumnFormat;
    tags?: string[];
}

export interface TabularReview {
  id: string;
  project_id: string | null;
  user_id: string;
  title: string | null;
  columns_config: ColumnConfig[] | null;
  workflow_id: string | null;
  practice?: string | null;
  /** Per-review email list. Used so standalone (project_id null) reviews can be shared directly. */
  shared_with?: string[];
  /** Server-set: true when the requesting user is the review's creator. */
  is_owner?: boolean;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  locked_by?: string | null;
  locked_at?: string | null;
  created_at: string;
  updated_at: string;
  document_count?: number;
}

export interface TabularCell {
  id: string;
  review_id: string;
  document_id: string;
  column_index: number;
  content: {
    summary: string;
    flag?: "green" | "grey" | "yellow" | "red";
    reasoning?: string;
  } | null;
  status: "pending" | "generating" | "done" | "error";
  created_at: string;
  edited_content?: {
    summary: string;
    flag?: "green" | "grey" | "yellow" | "red";
    reasoning?: string;
  } | null;
  edited_by?: string | null;
  edited_at?: string | null;
  user_comment?: string | null;
  comment_by?: string | null;
  comment_at?: string | null;
}

// Workflows

export interface AuditFlowWorkflow {
  id: string;
  user_id: string | null;
  title: string;
  type: "assistant" | "tabular";
  prompt_md: string | null;
  columns_config: ColumnConfig[] | null;
  is_system: boolean;
  created_at: string;
  practice?: string | null;
  shared_by_name?: string | null;
  allow_edit?: boolean;
  is_owner?: boolean;
}

// LAURA audit module

export type LauraStageId =
  | "p0" | "p1" | "p2" | "p3" | "p4" | "p5" | "p6" | "p7"
  | "e0" | "e1" | "e2" | "e3" | "e4" | "e5" | "e6" | "e7" | "e8";

export interface LauraSAIProfile {
  id: string;
  user_id: string | null;
  slug: string;
  name: string;
  is_builtin: boolean;
  default_language: string;
  core_instructions_override?: string | null;
  laura_sai_profile_documents?: {
    slot: string;
    document_id: string | null;
    builtin_path: string | null;
  }[];
  created_at: string;
  updated_at: string;
}

export interface LauraDeliverable {
  id: string;
  audit_id: string;
  stage: LauraStageId;
  content_md: string;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface LauraStageCompletion {
  id: string;
  audit_id: string;
  stage: LauraStageId;
  approved_at: string;
  approved_by: string;
}

export type LauraP0QuestionCategory =
  | "objeto"
  | "escopo"
  | "problema"
  | "recursos"
  | "complementar";

export type LauraP0Status =
  | "context"
  | "questions"
  | "answers"
  | "briefing"
  | "approved";

export interface LauraP0Question {
  id: string;
  category: LauraP0QuestionCategory;
  text: string;
  required: boolean;
  answer: string;
}

export interface LauraP0ValidationItem {
  id: string;
  text: string;
  checked: boolean;
  checked_at?: string;
}

export interface LauraP0Context {
  version: number;
  initial_comments?: string;
  status: LauraP0Status;
  questions?: LauraP0Question[];
  validation_items?: LauraP0ValidationItem[];
  questions_generated_at?: string;
  briefing_generated_at?: string;
  invalidated_at?: string;
}

export interface LauraP0State {
  p0: LauraP0Context;
  planning_input_count: number;
  documents: LauraAuditDocument[];
  deliverable: LauraDeliverable | null;
  completion: LauraStageCompletion | null;
  approved_by_display: string | null;
}

export interface LauraAudit {
  id: string;
  user_id: string;
  project_id: string | null;
  title: string;
  sai_profile_id: string;
  output_language: string;
  current_stage: LauraStageId;
  phase: "planning" | "execution";
  chat_id: string | null;
  planning_context?: Record<string, unknown>;
  shared_with?: string[];
  created_at: string;
  updated_at: string;
  laura_sai_profiles?: {
    slug: string;
    name: string;
    default_language: string;
  };
  stage_completions?: LauraStageCompletion[];
  deliverables?: LauraDeliverable[];
}

export interface LauraAuditDocument {
  id: string;
  audit_id: string;
  document_id: string;
  role: "planning_input" | "evidence" | "reference";
  evidence_meta?: {
    description?: string;
    date?: string | null;
    linked_questions?: string[];
    quality_notes?: string | null;
  } | null;
  documents?: AuditFlowDocument;
}

// API helpers

export interface AuditFlowChatDetailOut {
  chat: AuditFlowChat;
  messages: AuditFlowMessage[];
}

export interface TabularReviewDetailOut {
  review: TabularReview;
  cells: TabularCell[];
  documents: AuditFlowDocument[];
}
